import React, { useCallback, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import TextInput from '../TextInput';
import { errorMessage } from '../../utils/supabaseErrors';

// =======================================
// INTERFACES
// =======================================

interface NewContactCardProps {
  /** The company the new contact belongs to. */
  clientId: number;
  /**
   * The new contact's id, once it exists. The caller links it to its own
   * field, through the setter that knows that field's column name.
   */
  onCreated: (contactId: string, fullName: string) => void;
  onCancel: () => void;
}

// =======================================
// MAIN COMPONENT
// =======================================

/**
 * The inline "+ Add contact" form that opens under a contact field: name,
 * email and phone, created against the field's company. Inline rather than a
 * floating popover, which the modal's scrolling body clips near its edge.
 *
 * It owns what is typed into it, so closing it (unmounting) is all it takes
 * to forget a half-typed contact.
 */
const NewContactCard: React.FC<NewContactCardProps> = ({ clientId, onCreated, onCancel }) => {
  const supabase = useSupabaseClient();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canCreate = !isCreating && firstName.trim() !== '' && lastName.trim() !== '';

  const handleCreate = useCallback(async () => {
    if (!firstName.trim() || !lastName.trim()) return;

    setIsCreating(true);
    setError(null);
    try {
      const insertData: Record<string, string | number> = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        client_id: clientId,
      };
      if (email.trim()) insertData.email = email.trim();
      if (phone.trim()) insertData.phone = phone.trim();

      const { data, error: insertError } = await supabase
        .from('contacts')
        .insert(insertData)
        .select('id')
        .single();
      if (insertError) throw insertError;

      onCreated(String(data.id), `${firstName.trim()} ${lastName.trim()}`);
    } catch (err) {
      console.error('Error creating contact:', err);
      setError(errorMessage(err, 'Failed to create contact'));
      setIsCreating(false);
    }
  }, [firstName, lastName, email, phone, clientId, supabase, onCreated]);

  return (
    <div className="disclosure-card">
      <div className="disclosure-card__row">
        <TextInput
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name"
          aria-label="First name"
          disabled={isCreating}
          autoFocus
        />
        <TextInput
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Last name"
          aria-label="Last name"
          disabled={isCreating}
        />
      </div>
      <div className="disclosure-card__row">
        <TextInput
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          aria-label="Email"
          disabled={isCreating}
        />
        <TextInput
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone"
          aria-label="Phone"
          disabled={isCreating}
        />
      </div>
      {error && <p className="field__error" role="alert">{error}</p>}
      <div className="disclosure-card__actions">
        <button type="button" className="pill-btn pill-btn--ghost pill-btn--sm" onClick={onCancel} disabled={isCreating}>
          Cancel
        </button>
        <button
          type="button"
          className="pill-btn pill-btn--primary pill-btn--sm"
          onClick={handleCreate}
          disabled={!canCreate}
        >
          {isCreating ? 'Creating…' : 'Create contact'}
        </button>
      </div>
    </div>
  );
};

export default NewContactCard;
